import { useParams, Link } from "wouter";
import { InfoTip } from "@/components/ui/info-tip";
import { FORMULAS } from "@/lib/formulas";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  useGetInternalCatalogItemUsage,
  useUpdateInternalCatalogItem,
  useDeleteInternalCatalogItem,
  getListInternalCatalogQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { format } from "date-fns";
import {
  ArrowLeft, Edit, Trash2, Loader2, Wrench,
  DollarSign, Tag, Layers, TrendingDown, Hash, HardHat, Package, Receipt,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { cn } from "@/lib/utils";

const UNITS = ["each", "hour", "sqft", "lf", "day", "lb", "ton", "yd", "gal", "set", "trip"];

const COST_TYPES = ["labour", "materials", "fees"] as const;
type CostTypeValue = typeof COST_TYPES[number];

const TYPE_CONFIG: Record<CostTypeValue, { label: string; icon: React.ElementType }> = {
  labour: { label: "Labor", icon: HardHat },
  materials: { label: "Materials", icon: Package },
  fees: { label: "Fees", icon: Receipt },
};

const catalogSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  unitCost: z.coerce.number().min(0, "Cost must be 0 or more"),
  unit: z.string().optional(),
  category: z.string().optional(),
  type: z.enum(["labour", "materials", "fees"]),
});
type CatalogForm = z.infer<typeof catalogSchema>;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);

export default function CostCatalogDetail() {
  const params = useParams();
  const itemId = parseInt(params.id || "0", 10);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { data: usage, isLoading } = useGetInternalCatalogItemUsage(itemId, {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    query: { enabled: !!itemId } as any,
  });
  usePageTitle(usage?.name ?? "Cost Item");

  const updateItem = useUpdateInternalCatalogItem();
  const deleteItem = useDeleteInternalCatalogItem();

  const itemType = (COST_TYPES.includes((usage?.type ?? "materials") as CostTypeValue) ? (usage?.type ?? "materials") : "materials") as CostTypeValue;

  const form = useForm<CatalogForm>({
    resolver: zodResolver(catalogSchema),
    values: {
      name: usage?.name || "",
      description: usage?.description || "",
      unitCost: usage?.unitCost || 0,
      unit: usage?.unit || "each",
      category: usage?.category || "",
      type: itemType,
    },
  });

  const onUpdate = (data: CatalogForm) => {
    updateItem.mutate({
      id: itemId,
      data: {
        name: data.name,
        description: data.description || null,
        unitCost: data.unitCost,
        unit: data.unit || null,
        category: data.category || null,
        type: data.type,
      },
    }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListInternalCatalogQueryKey() });
        queryClient.invalidateQueries({ queryKey: [`/api/internal-catalog/${itemId}/usage`] });
        setIsEditOpen(false);
        toast({ title: "Cost item updated" });
      },
      onError: () => toast({ title: "Failed to update cost item", variant: "destructive" }),
    });
  };

  const onDelete = () => {
    deleteItem.mutate({ id: itemId }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListInternalCatalogQueryKey() });
        toast({ title: "Cost item deleted" });
        setLocation("/cost-catalog");
      },
      onError: () => toast({ title: "Failed to delete cost item", variant: "destructive" }),
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-8 w-64 mb-4" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-48" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!usage) {
    return <div className="text-center py-12">Cost item not found</div>;
  }

  const TypeIcon = TYPE_CONFIG[itemType].icon;

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <Link href="/cost-catalog">
        <Button variant="ghost" size="sm" className="gap-1.5 -ml-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" />
          Back to Cost Catalog
        </Button>
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Wrench className="w-6 h-6 text-muted-foreground" />
            <h1 className="text-3xl font-bold tracking-tight text-foreground">{usage.name}</h1>
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge variant="outline" className="gap-1 text-xs">
              <TypeIcon className="w-3 h-3" />
              {TYPE_CONFIG[itemType].label}
            </Badge>
            {usage.category && (
              <p className="text-muted-foreground flex items-center gap-1.5 text-sm">
                <Layers className="w-3.5 h-3.5" />
                {usage.category}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setIsEditOpen(true)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button variant="destructive" onClick={() => setIsDeleteOpen(true)}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Item Info */}
        <Card className="shadow-sm border-border/50 h-fit">
          <CardHeader>
            <CardTitle className="text-lg">Cost Item Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 text-sm">
              <DollarSign className="w-4 h-4 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium text-foreground">Unit Cost</p>
                <p className="text-2xl font-bold text-primary">{formatCurrency(usage.unitCost)}</p>
                {usage.unit && <p className="text-xs text-muted-foreground">per {usage.unit}</p>}
              </div>
            </div>

            {usage.description && (
              <div className="flex items-start gap-3 text-sm">
                <Tag className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Description</p>
                  <p className="text-muted-foreground mt-0.5">{usage.description}</p>
                </div>
              </div>
            )}

            <div className="pt-2 text-xs text-muted-foreground border-t border-border/50">
              Added {format(new Date(usage.createdAt), "MMMM d, yyyy")}
            </div>
          </CardContent>
        </Card>

        {/* Stats & History */}
        <div className="md:col-span-2 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="bg-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Hash className="w-3.5 h-3.5 text-muted-foreground" />
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">Times Used <InfoTip content={FORMULAS.catalogTimesUsed} /></p>
                </div>
                <p className="text-2xl font-bold">{usage.timesUsed}</p>
              </CardContent>
            </Card>
            <Card className="bg-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingDown className="w-3.5 h-3.5 text-muted-foreground" />
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">Total Internal Cost <InfoTip content={FORMULAS.catalogTotalCost} /></p>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(usage.totalCost)}</p>
              </CardContent>
            </Card>
          </div>

          <Card className="shadow-sm border-border/50">
            <CardHeader className="flex flex-row items-center justify-between pb-2 border-b bg-muted/40">
              <CardTitle className="text-lg">Usage History</CardTitle>
              {usage.usageHistory.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  {usage.usageHistory.length} entr{usage.usageHistory.length !== 1 ? "ies" : "y"}
                </span>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {usage.usageHistory.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">
                  This cost item hasn't been used in any quotes yet.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {usage.usageHistory.map((row, i) => (
                    <Link key={i} href={`/quotes/${row.estimateId}`}>
                      <div className="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer transition-colors group">
                        <div className="min-w-0 pr-4">
                          <div className="flex items-center gap-2">
                            {row.estimateNumber && (
                              <span className="text-xs font-mono text-muted-foreground">{row.estimateNumber}</span>
                            )}
                            <p className="font-medium truncate group-hover:text-primary transition-colors">
                              {row.estimateTitle}
                            </p>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {row.customerName} &middot; {format(new Date(row.estimateDate), "MMM d, yyyy")}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5 italic truncate">
                            Line item: {row.lineItemDescription}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-semibold">{formatCurrency(row.costTotal)}</p>
                          <p className="text-xs text-muted-foreground">qty: {row.qty}</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Cost Item</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onUpdate)} className="space-y-4">
              {/* Type selector */}
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type *</FormLabel>
                    <FormControl>
                      <div className="flex gap-2">
                        {COST_TYPES.map(t => {
                          const config = TYPE_CONFIG[t];
                          const Icon = config.icon;
                          return (
                            <button
                              key={t}
                              type="button"
                              onClick={() => field.onChange(t)}
                              className={cn(
                                "flex-1 flex flex-col items-center gap-1 py-2.5 px-2 rounded-lg border text-xs font-medium transition-all",
                                field.value === t
                                  ? "border-primary bg-primary/5 text-primary"
                                  : "border-border text-muted-foreground hover:border-border/80 hover:bg-muted/30"
                              )}
                            >
                              <Icon className="w-4 h-4" />
                              {config.label}
                            </button>
                          );
                        })}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Item Name *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Internal notes..." className="min-h-[60px]" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="unitCost"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit Cost ($) *</FormLabel>
                      <FormControl><Input type="number" step="0.01" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit</FormLabel>
                      <FormControl>
                        <select
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors"
                          {...field}
                        >
                          {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={updateItem.isPending}>
                  {updateItem.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Alert */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{usage.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This item will be removed from the catalog. Existing costs on quotes won't be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); onDelete(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteItem.isPending}
            >
              {deleteItem.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete Cost Item
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
